/**
 * Theme-Bruecke zwischen einbettender Seite (Parent) und eingebetteter App (Child).
 *
 * Warum ueberhaupt: Der geteilte Theme-Cookie (`janeway-theme`, Domain `.janewaylabs.com`)
 * wird beim LADEN des iframes mitgeschickt — Parent und Child starten also gleich. Toggelt
 * der Nutzer danach im Parent das Theme, liest das bereits geladene iframe den Cookie nicht
 * erneut und bliebe hell, waehrend die Huelle dunkel wird. Genau diese Luecke schliesst die
 * postMessage-Bruecke.
 *
 * Das Protokoll (`__janewayArtifactTheme`) existiert bereits zwischen ai-chat und drive --
 * hier wird es zum geteilten Baustein, statt es je Paarung neu zu erfinden.
 */
export type EmbedTheme = "light" | "dark";

/**
 * ── ANGEWENDET, NICHT GESPEICHERT ───────────────────────────────────────────
 *
 * Ein Theme, das ueber diese Bruecke hereinkommt, ist eine DARSTELLUNGSANWEISUNG
 * DES WIRTS, keine Entscheidung des Nutzers. Es faerbt das iframe — und darf die
 * gespeicherte Vorliebe des Kontos nicht anfassen.
 *
 * ── WARUM ES DIESE UNTERSCHEIDUNG BRAUCHT ───────────────────────────────────
 *
 * Die eingebettete App laeuft EINGELOGGT (der Sitzungskeks gilt fuer ganz
 * `.janewaylabs.com`) und mountet ihren eigenen `ColorThemeProvider`. Der
 * persistiert jede Aenderung von `resolvedTheme`, die nicht die erste Beobachtung
 * ist — und `useEmbeddedThemeBridge` ruft `setTheme()`, also sieht der Provider
 * eine ganz gewoehnliche Aenderung. Er KANN nicht unterscheiden, woher sie kam;
 * sein eigener Kommentar fuehrt die „iframe-Bridge" bereits als einen der Wege,
 * die an `setMode` vorbeilaufen.
 *
 * Gemessen am 2026-09-16 im Kong-Log (Labs, Konto des Betreibers, echter Chrome,
 * kein Agent): nach dem Oeffnen des GUI-Showrooms standen um 18:38:43 und
 * 18:39:01 zwei `POST user_preferences` mit `Referer: chat.janewaylabs.com` —
 * also aus den ECHTEN Apps, die der Showroom einbettet, nicht aus dem Showroom.
 *
 * Die Regel gilt fuer JEDE Einbettung, nicht nur fuer den Showroom: bettet
 * ai-chat morgen Kontakte ein und schickt sein Theme mit, darf das die Vorliebe
 * des Nutzers nicht ueberschreiben. Deshalb sitzt sie hier an der Bruecke und
 * nicht als `localOnly` an einem Ort.
 *
 * ── WIE ES FUNKTIONIERT ─────────────────────────────────────────────────────
 *
 * Die Bruecke merkt den Wert an, BEVOR sie ihn setzt; die eine Schleuse, durch
 * die der Provider persistiert, fragt ihn ab und verbraucht ihn dabei.
 *
 * **Ein WERT und kein blosses Flag** — aber nicht wegen eines Rennens. Hier
 * stand bis 2026-09-16 „ein Flag waere ein Rennen gegen den Effekt, der erst im
 * naechsten Commit laeuft"; nachgemessen stimmt das nicht, React faehrt den
 * Effekt ohnehin erst nach dem synchronen Block. Der echte Grund ist ein
 * anderer: ein Flag „gerade kommt was von aussen" wuesste nicht, WELCHER Wert
 * gemeint war, und verschluckte damit die naechste Aenderung — auch die des
 * Nutzers. Der Wert trifft nur den einen Vorgang, den er deckt.
 *
 * **Verbraucht wird IMMER, auch bei Nichttreffer.** Sonst bliebe eine Marke
 * liegen, wenn der Effekt nie feuert, und der naechste echte Klick des Nutzers
 * auf genau diesen Wert wuerde still verschluckt.
 */
let themeVomWirt: EmbedTheme | null = null;

/** Bruecke: „dieser Wert kommt vom Wirt" — vor dem Anwenden. */
export function merkeThemeVomWirt(theme: EmbedTheme): void {
  themeVomWirt = theme;
}

/**
 * Persistenz-Schleuse: „war dieser Wert eine Anweisung des Wirts?" — und raeumt
 * die Marke weg. `true` heisst: anwenden ja, speichern nein.
 */
export function verbraucheThemeVomWirt(theme: EmbedTheme): boolean {
  const vomWirt = themeVomWirt === theme;
  themeVomWirt = null;
  return vomWirt;
}

/** Nur fuer Tests: die Marke ohne Abfrage wegnehmen. */
export function vergissThemeVomWirt(): void {
  themeVomWirt = null;
}

export interface EmbedThemeMessage {
  __janewayArtifactTheme: EmbedTheme;
}

export function isEmbedThemeMessage(data: unknown): data is EmbedThemeMessage {
  const theme = (data as EmbedThemeMessage | null)?.__janewayArtifactTheme;
  return theme === "dark" || theme === "light";
}

/** Liest das aktuell im Dokument aktive Theme (next-themes setzt `.dark` auf <html>). */
export function currentDocumentTheme(): EmbedTheme {
  if (typeof document === "undefined") return "dark";
  const el = document.documentElement;
  return el.classList.contains("dark") || el.getAttribute("data-theme") === "dark" ? "dark" : "light";
}

/**
 * Parent-Seite: schickt das eigene Theme an ein eingebettetes iframe.
 *
 * `targetOrigin` ist die Origin des iframes -- kein `"*"`: das Theme ist zwar kein Geheimnis,
 * aber ein `"*"`-Post geht auch an eine fremde Seite, die das iframe unterwandert hat.
 */
export function postEmbeddedTheme(
  frame: HTMLIFrameElement | null,
  targetOrigin: string,
  theme: EmbedTheme = currentDocumentTheme(),
): void {
  const win = frame?.contentWindow;
  if (!win) return;
  const message: EmbedThemeMessage = { __janewayArtifactTheme: theme };
  win.postMessage(message, targetOrigin);
}

/* ══════════════════════════════════════════════════════════════════════════
   DAS FARBTHEMA — die zweite Achse, und bis 0.442.2 fuhr sie gar nicht mit.

   Die Nachricht oben traegt den MODUS (hell/dunkel). Gemessen am 2026-09-17
   im laufenden Showroom, Wirt auf „Claude"/dunkel, alle Inline-Token an
   beiden Wurzelknoten verglichen:

       29 Token im Wirt, 28 im Kind — 28 weichen ab.
       Der Modus stimmt. Sonst nichts.

   15 davon deckt die Flaechen-Bruecke ab, sobald sie laeuft. **Zwoelf bleiben**,
   und es sind nicht die Nebensaechlichen:

       --primary                hsl(18 57% 46%)  →  hsl(0 0% 98%)
       --primary-foreground     hsl(0 0% 100%)   →  hsl(0 0% 9%)
       --ring                   hsl(18 57% 59%)  →  hsl(0 0% 37%)
       --secondary              hsl(40 9% 10%)   →  hsl(0 0% 12%)
       --secondary-foreground   hsl(40 13% 76%)  →  hsl(0 0% 98%)
       --destructive            hsl(0 72% 72%)   →  hsl(0 72% 60%)
       --destructive-foreground hsl(0 0% 18%)    →  hsl(0 0% 1%)
       --sidebar-primary(-foreground), --sidebar-accent(-foreground), --sidebar-ring

   Also die ganze Marken- und Handlungsfarbe, das Sekundaer- und das
   Destruktivpaar.

   ── WARUM DIE ID UND NICHT DIE WERTE ───────────────────────────────────────

   Eine „kleine Erweiterung" um `--primary` liesse elf Token stehen — derselbe
   Fehler in klein. Und selbst die 15 „gedeckten" waeren unter der
   Flaechen-Bruecke nicht die Werte des Wirts, sondern Abstufungen seiner
   gemessenen Flaeche (`--card` = `schritt(0.06)` aus dem Flaechen-Hex, nicht
   `hsl(40 9% 17%)`). Nah dran ist nicht dasselbe.

   Das Kind hat dieselbe Themen-Tabelle wie der Wirt — es loest die ID selbst
   auf und trifft alle 29 Token exakt, samt der per-Variable-Overrides des
   Nutzers. Eine ID ist ausserdem kurz genug fuer die Adresse.

   ── WAS DAMIT NICHT GELOEST IST, UND WARUM ES HIER STEHT ───────────────────

   **Das erste Bild kommt vom Server, und der kennt die ID nicht.** Ein Layout
   bekommt in Next 16 keine `searchParams` („Layouts do not rerender on
   navigation"), und ein Vorab-Skript im Kopf kann die Themen-Tabelle nicht
   importieren. Die Flaeche steht beim ersten Bild richtig (sie reist an der
   Adresse und wird vom Vorpaint-Skript gesetzt); die zwoelf Akzentfarben
   ziehen nach der Hydration nach.

   Wer das schliessen will, schickt die AUFGELOESTEN Werte statt der ID — dann
   kann das Vorpaint-Skript sie ohne Tabelle setzen. Preis: die Themen-
   Aufloesung stuende an zwei Stellen. Bewusst nicht gebaut, bis jemand den
   Nachzug im Bild sieht.
   ══════════════════════════════════════════════════════════════════════════ */

/** Query-Parameter, mit dem der Einbetter sein Farbthema an die Anfrage haengt. */
export const EMBED_FARBTHEMA_PARAM = "farbthema";

export interface EmbedFarbthemaMessage {
  __janewayEmbedFarbthema: string;
}

/**
 * Erlaubt sind ausschliesslich die Zeichen, aus denen Themen-IDs bestehen.
 *
 * Der Wert kommt aus einer URL und aus einer Fensternachricht. `getTheme` faellt
 * bei einer unbekannten ID zwar auf das erste Thema zurueck, ist also selbst
 * nicht gefaehrlich — aber der Wert landet auch als Datenattribut im DOM, und
 * eine feste Form ist dort dieselbe Entscheidung wie bei `istFlaechenwert`:
 * ein unbekannter Wert wird VERWORFEN, bevor er irgendwo landet.
 */
const FARBTHEMA_MUSTER = /^[a-z0-9][a-z0-9-]{0,39}$/;

export function istFarbthemaId(wert: unknown): wert is string {
  return typeof wert === "string" && FARBTHEMA_MUSTER.test(wert);
}

export function isEmbedFarbthemaMessage(data: unknown): data is EmbedFarbthemaMessage {
  return istFarbthemaId((data as EmbedFarbthemaMessage | null)?.__janewayEmbedFarbthema);
}

/**
 * Parent-Seite: meldet dem eingebetteten iframe sein Farbthema.
 *
 * `targetOrigin` ist die aufgeloeste Ziel-Origin, **nie `"*"`** — woertlich
 * dieselbe Zusage wie `postEmbeddedTheme` und `postEmbeddedFlaeche`.
 */
export function postEmbeddedFarbthema(
  frame: HTMLIFrameElement | null,
  targetOrigin: string,
  farbthema: string,
): void {
  const win = frame?.contentWindow;
  if (!win) return;
  if (!targetOrigin || targetOrigin === "*") return;
  if (!istFarbthemaId(farbthema)) return;
  const message: EmbedFarbthemaMessage = { __janewayEmbedFarbthema: farbthema };
  win.postMessage(message, targetOrigin);
}

/** Child-Seite: das Farbthema aus der eigenen Adresse lesen (der Weg fuers erste Bild). */
export function farbthemaAusUrl(search: string): string | null {
  const wert = new URLSearchParams(search).get(EMBED_FARBTHEMA_PARAM);
  return istFarbthemaId(wert) ? wert : null;
}

/** Parent-Seite: den Parameter an eine Einbettungs-Adresse haengen. */
export function mitFarbthemaInDerUrl(url: string, farbthema: string | null): string {
  if (!istFarbthemaId(farbthema)) return url;
  const trenner = url.includes("?") ? "&" : "?";
  return `${url}${trenner}${EMBED_FARBTHEMA_PARAM}=${encodeURIComponent(farbthema)}`;
}
